import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const getBhutanDate = (date = new Date()) =>
  new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Thimphu' }));

const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Dynamic year options — from app start (2025) through currentYear + 2
export const getYearOptions = () => {
  const currentYear = getBhutanDate().getFullYear();
  const startYear = 2025;
  const endYear = currentYear + 2;
  const years = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);
  return years;
};

// Current Bhutan year helper for default selections
export const getCurrentYear = () => getBhutanDate().getFullYear();

const getMonthRange = () => {
  const now = getBhutanDate();
  const y = now.getFullYear(), m = now.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  return { start: fmt(start), end: fmt(end) };
};

const PRESETS = {
  today: () => { const t = fmt(getBhutanDate()); return { start: t, end: t }; },
  week: () => {
    const now = getBhutanDate();
    const s = new Date(now); s.setDate(now.getDate() - 6);
    return { start: fmt(s), end: fmt(now) };
  },
  month: getMonthRange,
  last30: () => {
    const e = getBhutanDate();
    const s = new Date(e); s.setDate(e.getDate() - 30);
    return { start: fmt(s), end: fmt(e) };
  },
};

const PeriodContext = createContext(null);

export function PeriodProvider({ children }) {
  const [range, setRange] = useState(() => PRESETS.today());
  const [preset, setPreset] = useState('today');

  const applyPreset = useCallback((key) => {
    const fn = PRESETS[key];
    if (fn) { setRange(fn()); setPreset(key); }
  }, []);

  const setCustomRange = useCallback((start, end) => {
    setRange({ start, end });
    setPreset('custom');
  }, []);

  const value = useMemo(() => ({
    range,
    preset,
    applyPreset,
    setCustomRange,
    queryString: `start_date=${range.start}&end_date=${range.end}`,
  }), [range, preset, applyPreset, setCustomRange]);

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod() {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error('usePeriod must be used within PeriodProvider');
  return ctx;
}
